import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

const ProfileTab = () => {
  const { user, profile, updateProfile } = useAuth();
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    timezone: 'America/New_York',
    language: 'en'
  });
  const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [passwordSuccessMsg, setPasswordSuccessMsg] = useState('');
  const [passwordErrorMsg, setPasswordErrorMsg] = useState('');

  useEffect(() => {
    if (profile) {
      const nameParts = (profile?.full_name || '')?.split(' ');
      setProfileData(prev => ({
        ...prev,
        firstName: nameParts?.[0] || '',
        lastName: nameParts?.slice(1)?.join(' ') || '',
        email: profile?.email || '',
        jobTitle: profile?.title || ''
      }));
    }
  }, [profile]);

  const timezoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
    { value: 'Europe/Paris', label: 'Central European Time (CET)' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' }
  ];

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' }
  ];

  const handleProfileChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);
    setSuccessMsg('');
    setErrorMsg('');
    try {
      const fullName = `${profileData?.firstName} ${profileData?.lastName}`?.trim();
      const { error } = await updateProfile({ full_name: fullName });
      if (error) throw error;
      setSuccessMsg('Profile saved successfully!');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setErrorMsg(err?.message || 'Failed to save profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordErrorMsg('');
    setPasswordSuccessMsg('');

    if (!passwordData?.newPassword || !passwordData?.confirmPassword) {
      setPasswordErrorMsg('Please fill in all password fields');
      return;
    }
    if (passwordData?.newPassword !== passwordData?.confirmPassword) {
      setPasswordErrorMsg('Passwords do not match');
      return;
    }
    if (passwordData?.newPassword?.length < 8) {
      setPasswordErrorMsg('Password must be at least 8 characters');
      return;
    }

    setPasswordLoading(true);
    try {
      // Refresh session first to ensure valid access token
      const { data: sessionData, error: sessionError } = await supabase?.auth?.refreshSession();
      if (sessionError || !sessionData?.session) {
        setPasswordErrorMsg('Your session has expired. Please log out and log in again, then try changing your password.');
        return;
      }

      const { error } = await supabase?.auth?.updateUser({ password: passwordData?.newPassword });
      if (error) {
        if (error?.message?.includes('same password') || error?.message?.includes('different from the old')) {
          setPasswordErrorMsg('New password must be different from your current password.');
        } else if (error?.message?.includes('session') || error?.message?.includes('token')) {
          setPasswordErrorMsg('Session expired. Please log out and log in again to change your password.');
        } else {
          setPasswordErrorMsg(error?.message || 'Failed to change password. Please try again.');
        }
        return;
      }

      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordSuccessMsg('Password changed successfully!');
      setTimeout(() => setPasswordSuccessMsg(''), 4000);
    } catch (err) {
      setPasswordErrorMsg(err?.message || 'Failed to change password. Please try again.');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {successMsg && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">{successMsg}</div>
      )}
      {errorMsg && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{errorMsg}</div>
      )}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center space-x-4 mb-6">
          <Icon name="User" size={24} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Profile Information</h3>
            <p className="text-sm text-muted-foreground">Update your personal details and preferences</p>
          </div>
        </div>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="First Name" type="text" value={profileData?.firstName}
              onChange={(e) => handleProfileChange('firstName', e?.target?.value)} required />
            <Input label="Last Name" type="text" value={profileData?.lastName}
              onChange={(e) => handleProfileChange('lastName', e?.target?.value)} required />
            <Input label="Email Address" type="email" value={profileData?.email}
              onChange={(e) => handleProfileChange('email', e?.target?.value)} disabled />
            <Input label="Job Title" type="text" value={profileData?.jobTitle}
              onChange={(e) => handleProfileChange('jobTitle', e?.target?.value)} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select label="Timezone" options={timezoneOptions} value={profileData?.timezone}
              onChange={(value) => handleProfileChange('timezone', value)} searchable />
            <Select label="Language" options={languageOptions} value={profileData?.language}
              onChange={(value) => handleProfileChange('language', value)} />
          </div>
          <div className="flex justify-end">
            <Button variant="default" onClick={handleSaveProfile} loading={isLoading} iconName="Save" iconPosition="left">
              Save Profile
            </Button>
          </div>
        </div>
      </div>
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center space-x-4 mb-6">
          <Icon name="Lock" size={24} className="text-primary" />
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Change Password</h3>
            <p className="text-sm text-muted-foreground">Update your account password</p>
          </div>
        </div>
        {passwordErrorMsg && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {passwordErrorMsg}
          </div>
        )}
        {passwordSuccessMsg && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
            {passwordSuccessMsg}
          </div>
        )}
        <div className="space-y-4">
          <Input label="New Password" type="password" value={passwordData?.newPassword}
            onChange={(e) => setPasswordData(prev => ({...prev, newPassword: e?.target?.value}))} />
          <Input label="Confirm New Password" type="password" value={passwordData?.confirmPassword}
            onChange={(e) => setPasswordData(prev => ({...prev, confirmPassword: e?.target?.value}))} />
          <div className="flex justify-end">
            <Button variant="default" onClick={handleChangePassword} loading={passwordLoading}
              disabled={!passwordData?.newPassword || !passwordData?.confirmPassword}>
              Change Password
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileTab;